
import { Component, Vue, Watch } from 'vue-property-decorator';
import Breadcrumbs from '@/views/coach/Breadcrumbs.vue';
import { memberRoute, planningRoute, schemeRoute } from '@/router/coach';
import { Route } from 'vue-router';
import { EventBus } from '@/services/event-bus';
import Header from '@/components/common/Header.vue';
import Navigation from '@/components/common/Navigation.vue';
import GlobalAlert from '@/components/common/GlobalAlert.vue';
import CookieConsentBanner from '@/components/common/CookieConsentBanner.vue';
import NewVersionDialog from '@/components/common/NewVersionDialog.vue';
import { overrideDefaultTranslations } from '@/services/translation';
import { sendGALoginEvent } from '@/services/utils';
import { applyClientStylesheet } from '@/services/theme';
import { isProtectedPath } from './services/auth';
import { db } from '@/services/db';
import { useMemberTagsStore } from '@/stores/member-tags';
import { useMaintenanceMode } from '@/composables/use-online';
import MaintenanceMode from '@/components/common/MaintenanceMode.vue';

@Component({
  components: {
    Header,
    Navigation,
    Breadcrumbs,
    GlobalAlert,
    CookieConsentBanner,
    NewVersionDialog,
    MaintenanceMode,
  },
})
export default class App extends Vue {
  public drawer = null;
  private loading = false;
  private memberTagsStore = useMemberTagsStore();
  private isAutomatedBrowser = navigator.webdriver;

  get currentRouteFullPath() {
    return this.$route.fullPath;
  }

  get maintenanceMode() {
    return useMaintenanceMode().value;
  }

  /**
   * Created lifecycle hook, called automatically.
   * Dispatches the stores action to fetch and populate the store cache for the scheme tree.
   *
   * @return {void}
   */
  async created(): Promise<void> {
    this.createEventBusListeners();
    await this.initializeStore();
    await this.initializeTranslations();
  }

  async createEventBusListeners() {
    EventBus.$on('update-token', () => {
      this.$store.commit('auth/retreiveAndUpdateToken');
    });
    EventBus.$on('user-login', async () => {
      await this.initializeStore();
      if (this.$gtag) {
        sendGALoginEvent(
          this.$gtag,
          this.$store.getters['auth/loggedInUser'],
          'Coach Portal Login',
          process.env.VUE_APP_COACH_GOOGLE_ANALYTICS_ID
        );
      }
    });
    EventBus.$on('translations-initialized', async () => {
      document.title = this.$t('login.coach_portal');
    });
    EventBus.$on('invalid-token', async () => {
      await this.$store.dispatch('auth/logout');
      if (isProtectedPath(this.$route.path)) {
        this.$router.push({ name: 'login' }).catch(() => {
          // ignore routing errors
        });
      }
    });
    EventBus.$on('check-online-status', async () => {
      db.checkIsOnline();
    });
  }

  get groupsLoading() {
    return this.$store.getters['groups/isLoading'] || this.$store.getters['groupMembers/isLoading'];
  }

  async initializeStore() {
    this.loading = true;
    const loggedIn = await this.$store.dispatch('auth/initialize');
    const client = this.$store.getters['auth/clientDetails'];
    applyClientStylesheet(client?.theme_url);
    if (!client) {
      this.$alert('error', 'Invalid hostname, please contact support');
    }

    if (loggedIn) {
      await this.$store.dispatch('groups/syncWithStoreSessionDate');
      this.$store.dispatch('groups/fetchItems'); // load groups and active schemes -- do in background
      // we stop showing the loading bar once both the below data is loaded AND the list of groups and schemes is loaded.
      // we do not show it for group-member loading.

      await Promise.all([
        this.$store.dispatch('labels/fetchLabels'),
        this.$store.dispatch('clubs/fetchClubs'),
        this.memberTagsStore.fetchMemberTags(),
      ]);
      this.$store.dispatch('schemes/initializeAdditionalAssessmentNodeIds');
      this.$store.commit('nav/updateMemberTabLastVisitedRoute', memberRoute);
      this.$store.commit('nav/updateSchemeTabLastVisitedRoute', schemeRoute);
      this.$store.commit('nav/updatePlanningTabLastVisitedRoute', planningRoute);
    }
    await this.updateStoreFromUrl(this.$router.currentRoute);
    EventBus.$emit('user-initialized');
    this.loading = false;
  }

  /**
   * Merge the two translation files ready for use
   * Main file hosted in locales directory
   * Override file hosted on VUE_APP_TRANSLATION_URL
   * will only contain changes to the original en.json file
   */
  async initializeTranslations() {
    this.loading = true;
    const client = this.$store.getters['auth/clientDetails'];
    const translationUrl = client ? client.translation_url : '';
    const localeMessages = await overrideDefaultTranslations(this.$i18n.locale, this.$i18n.messages, translationUrl);
    this.$i18n.setLocaleMessage(this.$i18n.locale, localeMessages);
    EventBus.$emit('translations-initialized');
    this.loading = false;
  }

  @Watch('$route', { immediate: true, deep: true })
  /**
   * Called when the route changes, responsible updating breadcrumbs and the last visited route.
   *
   * @return {void}
   */
  onUrlChange(newRoute: Route): void {
    this.updateStoreFromUrl(newRoute);
  }

  updateStoreFromUrl(newRoute: Route) {
    if (this.isSchemeRoute) {
      const routeParams = newRoute.params as { schemeSlug: string; schemeNodeId: string };
      const nodeId = typeof routeParams.schemeNodeId !== 'undefined' ? '/' + routeParams.schemeNodeId : null;
      const schemeSlug = typeof routeParams.schemeSlug !== 'undefined' ? routeParams.schemeSlug : null;
      this.$store.commit('nav/updateSchemeTabLastVisitedRoute', newRoute.path);
      this.$store.dispatch('nav/calculateStyle', { schemeSlug, schemeNodeId: nodeId, updateTheme: true });
    } else {
      this.$store.dispatch('nav/calculateStyle', { schemeSlug: null, schemeNodeId: null, updateTheme: true }); // always want a theme not necessarily based on a nodeId though
    }
    if (this.isMemberRoute) {
      this.$store.commit('nav/updateMemberTabLastVisitedRoute', newRoute);
      this.$store.dispatch('nav/updateMemberBreadcrumbs', newRoute);
    }
  }

  /**
   * Returns the string that defines the member route, this is a const and can be used for testing the tab the
   * route relates to.
   *
   * @return {string}
   */
  get memberRoute(): string {
    return memberRoute;
  }

  /**
   * Returns the string that defines the member route, this is a const and can be used for testing the tab the
   * route relates to.
   *
   * @return {string}
   */
  get isMemberRoute(): boolean {
    return this.$route.path.startsWith(memberRoute);
  }

  /**
   * Returns the string that defines the planning route, this is a const and can be used for testing the tab the
   * route relates to.
   *
   * @return {string}
   */
  get planningRoute(): string {
    return planningRoute;
  }

  /**
   * Tests if the route is related to the planning tab.
   *
   * @return {boolean}
   */
  get isPlanningRoute(): boolean {
    return this.$route.path.startsWith(planningRoute);
  }

  /**
   * Returns the last visited route on the groups tab.  The last visited route is updated by the Nav component.
   *
   * @return {Route}
   */
  get membersLastVisitedRoute(): Route {
    return this.$store.getters['nav/memberTabLastVisitedRoute'];
  }

  /**
   * Tests if the route is related to the scheme tab.
   *
   * @return {boolean}
   */
  get isSchemeRoute(): boolean {
    return this.$route.path.startsWith(schemeRoute);
  }

  /**
   * Returns the last visited route on the planning tab.  The last visited route is updated by the Nav component.
   *
   * @return {Route}
   */
  get planningLastVisitedRoute(): Route {
    return this.$store.getters['nav/planningTabLastVisitedRoute'];
  }

  /**
   * Returns the last visited route on the scheme tab.  The last visited route is updated by the Nav component.
   *
   * @return {string}
   */
  get schemeLastVisitedRoute(): string {
    return this.$store.getters['nav/schemeTabLastVisitedRoute'];
  }

  get isLoggedIn() {
    return this.$store.getters['auth/isLoggedIn']();
  }

  /**
   * Adjusts style by adding classes, based on possible external theme and possible content or route related changes
   */
  get themeStyle(): string | null {
    return this.$store.getters['nav/themeStyle'];
  }
}
